import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Clock, Eye, Play } from 'lucide-react';

interface CoverageItem {
	id: string;
	creator_id: string;
	game_id: string;
	key_request_id: string;
	title: string;
	thumbnail_url: string;
	video_url: string;
	platform: 'youtube' | 'twitch';
	posted_date: string;
	views: number;
	tags: string[];
	created_at: string;
	game?: {
		id: string;
		title: string;
		cover_image_url?: string;
	};
}

interface UserProfile {
	twitch_connected: boolean;
	youtube_connected: boolean;
	twitch_username?: string;
	youtube_username?: string;
}

interface CoverageContentProps {
	coverageItems: CoverageItem[];
	userProfile: UserProfile | null;
	isLoading?: boolean;
}

export function CoverageContent({ coverageItems, userProfile, isLoading = false }: CoverageContentProps) {
	const formatViewCount = (count?: number) => {
		if (!count) return '0';
		if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
		if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
		return count.toString();
	};

	const getPlatformColor = (platform: string) => {
		switch (platform) {
			case 'youtube':
				return 'bg-red-500';
			case 'twitch':
				return 'bg-purple-500';
			default:
				return 'bg-gray-500';
		}
	};

	const getPlatformIcon = (platform: string) => {
		switch (platform) {
			case 'youtube':
				return '📺';
			case 'twitch':
				return '🎮';
			default:
				return '📹';
		}
	};

	return (
		<div className="space-y-6">
			<div className="mb-8">
				<h2 className="text-2xl font-bold mb-2">Coverage</h2>
				<p className="text-muted-foreground">
					Manage and view your content across different platforms
				</p>
			</div>

			{/* Connection Status */}
			<Card className="p-6">
				<h3 className="text-lg font-semibold mb-4">Platform Connections</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div className="flex items-center justify-between p-4 border rounded-custom-paddings">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 bg-red-500 rounded-custom-paddings flex items-center justify-center text-white font-bold">
								📺
							</div>
							<div>
								<p className="font-medium">YouTube</p>
								<p className="text-sm text-muted-foreground">
									{userProfile?.youtube_connected === true ? 'Connected' : 'Not connected'}
								</p>
							</div>
						</div>
						<Badge variant={userProfile?.youtube_connected === true ? 'default' : 'secondary'}>
							{userProfile?.youtube_connected === true ? 'Active' : 'Inactive'}
						</Badge>
					</div>

					<div className="flex items-center justify-between p-4 border rounded-custom-paddings">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 bg-purple-500 rounded-custom-paddings flex items-center justify-center text-white font-bold">
								🎮
							</div>
							<div>
								<p className="font-medium">Twitch</p>
								<p className="text-sm text-muted-foreground">
									{userProfile?.twitch_connected === true ? 'Connected' : 'Not connected'}
								</p>
							</div>
						</div>
						<Badge variant={userProfile?.twitch_connected === true ? 'default' : 'secondary'}>
							{userProfile?.twitch_connected === true ? 'Active' : 'Inactive'}
						</Badge>
					</div>
				</div>

				{(!userProfile?.youtube_connected && !userProfile?.twitch_connected) && (
					<div className="mt-4 p-4 bg-muted/50 rounded-lg">
						<div className="flex items-center gap-2 text-muted-foreground">
							<AlertCircle className="w-4 h-4" />
							<p className="text-sm">
								Connect your platforms in Settings to view your content here.
							</p>
						</div>
					</div>
				)}
			</Card>

			{/* Content List */}
			{coverageItems.length > 0 && !isLoading && (
				<div className="space-y-4">
					<h3 className="text-lg font-semibold">Coverage Content</h3>
					{coverageItems.map((item) => (
						<Card key={item.id} className="bg-muted/20 border dark:bg-green-950/20 dark:border-green-800 hover:bg-muted/50 p-4 rounded-lg shadow-sm text-card-foreground transition-colors">
							<div className="flex flex-col md:flex-row gap-4">
								<div className="relative flex-shrink-0 cursor-pointer w-full md:w-auto h-28 overflow-hidden rounded-lg" onClick={() => window.open(item.video_url, '_blank')}>
									<img
										src={item.thumbnail_url}
										alt={item.title}
										className="w-full h-full object-cover hover:opacity-90 transition-opacity"
									/>
									<div className="absolute top-1 left-1">
										<Badge className={`${getPlatformColor(item.platform)} text-white`}>
											{getPlatformIcon(item.platform)}
										</Badge>
									</div>
								</div>
								
								<div className="flex-1 min-w-0">
									<h4 
										className="font-semibold truncate mb-1 cursor-pointer hover:text-primary transition-colors" 
										onClick={() => window.open(item.video_url, '_blank')}
									>
										{item.title}
									</h4>
									
									<div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
										<div className="flex items-center gap-1">
											<Eye className="w-4 h-4" />
											{formatViewCount(item.views)} views
										</div>
										<div className="flex items-center gap-1">
											<Clock className="w-4 h-4" />
											{new Date(item.posted_date).toLocaleDateString()}
										</div>
									</div>

									{/* Hashtags for YouTube videos */}
									{item.platform === 'youtube' && item.tags && item.tags.length > 0 && (
										<div className="flex flex-wrap gap-1">
											{item.tags.map((tag, index) => (
												<Badge key={index} variant="secondary" className="text-[10px] px-1 py-0">
													#{tag}
												</Badge>
											))}
										</div>
									)}
								</div>
							</div>
						</Card>
					))}
				</div>
			)}

			{coverageItems.length === 0 && !isLoading && (
				<Card className="p-8 text-center">
					<div className="text-muted-foreground">
						<Play className="w-12 h-12 mx-auto mb-4 opacity-50" />
						<p className="text-lg font-medium mb-2">No content found</p>
						<p className="text-sm">
							Click "Refresh Content" in Settings to load your latest videos and streams.
						</p>
					</div>
				</Card>
			)}
		</div>
	);
}

